import React, { useEffect, useState } from "react";
import { Box, Avatar, IconButton, CircularProgress, Typography, Stack, Dialog } from "@mui/material";
import { PhotoCamera, Close } from "@mui/icons-material";
import axios from "axios";
import { API_PROFILE_UPLOAD } from "../../Util/URLConstants";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_ID_KEY, SESSION_USER_IMAGE_KEY } from "../../Util/VMConstants";
import { isEmpty } from "../../Util/UtilHelper";
import { VM_Colors } from "../../Styles/VMStyles";
import MUITypography from "../../Components/MUITypography";

function ProfileComponent({ userState, loadProfile }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  useEffect(() => {
    if (!isEmpty(userState?.image_url)) {
      // keep preview exactly as received (no cache-buster here)
      setPreview(userState.image_url);
    } else {
      setPreview(null);
    }
  }, [userState]);

  const getInitials = (name = "") => {
    return name
      .split(" ")
      .map((n) => n[0]?.toUpperCase())
      .slice(0, 2)
      .join("");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage("Only image files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage("File size must be under 5MB.");
      return;
    }

    setUploading(true);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", userID);

      const response = await axios.post(API_PROFILE_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.status_code === 200) {
        
        const uploadedUrl = response.data.data.file_url || URL.createObjectURL(file);

        // cache-bust the URL saved to session (so Navbar will re-fetch)
        const cacheBusted = uploadedUrl.includes("?")
          ? `${uploadedUrl}&t=${Date.now()}`
          : `${uploadedUrl}?t=${Date.now()}`;

        // 1) update preview shown in this component
        setMessage("Uploaded successfully!");
        setPreview(uploadedUrl);

        // 2) update session storage so Navbar can read the new image
        // SAVE cache-busted URL (important)
        sessionFactory.add(SESSION_USER_IMAGE_KEY, cacheBusted);

        // 3) notify same-tab listeners and pass the new URL
        window.dispatchEvent(
          new CustomEvent("userImageUpdated", { detail: { image: cacheBusted } })
        );

        // 4) optionally reload user info on this page, if parent provided loadProfile
        if (typeof loadProfile === "function") {
          // small delay to allow server to persist if needed
          setTimeout(() => loadProfile(), 250);
        }
      } else {
        setMessage(response.data.message || "Upload failed.");
      }
    } catch (err) {
      console.error(err);
      setMessage("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 320,
        mx: "auto",
        mt: 1,
        p: 1,
        textAlign: "center",
      }}
    >
      <MUITypography sx={{ Padding: "10px", fontSize: '20px' }}>
        Profile
      </MUITypography>

      <Stack spacing={2} alignItems="center">
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Avatar
            src={preview || ""}
            alt="Profile"
            sx={{
              width: 120,
              height: 120,
              fontSize: 36,
              border: "0.1px dotted gray",
              cursor: preview ? "pointer" : "default",
            }}
            onClick={() => preview && setOpenDialog(true)}
          >
            {!preview && getInitials(userState?.name || "User")}
          </Avatar>

          <input
            type="file"
            accept="image/*"
            id="profile-upload"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <label htmlFor="profile-upload">
            <IconButton
              component="span"
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                bgcolor: VM_Colors.primary,
                color: "white",
                boxShadow: 2,
                "&:hover": { bgcolor: "#162e66" },
              }}
            >
              {uploading ? <CircularProgress size={20} sx={{ color: "white" }} /> : <PhotoCamera fontSize="small" />}
            </IconButton>
          </label>
        </Box>

        {message && (
          <Typography variant="body2" color="success.main">
            {message}
          </Typography>
        )}
      </Stack>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md">
        <Box sx={{ position: "relative", bgcolor: "black" }}>
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "white",
              backgroundColor: VM_Colors.dark_gradient,
              "&:hover": { backgroundColor: VM_Colors.dark_gradient },
              zIndex: 2,
            }}
          >
            <Close />
          </IconButton>
          <Box
            component="img"
            src={preview}
            alt="Profile Fullscreen"
            sx={{
              maxHeight: "90vh",
              maxWidth: "90vw",
              objectFit: "contain",
              display: "block",
              margin: "auto",
            }}
          />
        </Box>
      </Dialog>
    </Box>
  );
}

export default ProfileComponent;
